import React, { useEffect, useState, useRef, useMemo } from "react";
import AsyncSelect from "react-select/async";
import { useDispatch, useSelector } from "react-redux";
import { getCustomersAction } from "../../../store/actions/customer/CustomerActions";

const SearchableCustomerSelect = ({ onSelect, value, placeholder = "Search Customers...", controlled = false }) => {
  const dispatch = useDispatch();
  const { customers } = useSelector((state) => state.customer);
  const customerRows = useMemo(() => customers?.rows || [], [customers]);

  const [inputValue, setInputValue] = useState("");
  const [selectedValue, setSelectedValue] = useState(null);
  const latestCallback = useRef(null);

  // Determine if this is controlled mode (new behavior) or uncontrolled mode (old behavior)
  const isControlled = controlled || value !== undefined;

  // Load data when inputValue changes
  useEffect(() => {
    if (inputValue) {
      const search = inputValue ? inputValue : '';
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const queryString = `${params.toString()}`;
      dispatch(getCustomersAction(1, 10, "", queryString));
    }
  }, [inputValue, dispatch]);

  // Sync selectedValue with external value prop (only in controlled mode)
  useEffect(() => {
    if (!isControlled) return; // Skip in uncontrolled mode
    
    if (!value) {
      setSelectedValue(null);
    } else if (value !== selectedValue?.value) {
      // Find the customer in the current customerRows
      const customer = customerRows.find(cust => cust._id === value);
      if (customer) {
        setSelectedValue({
          label: customer.name+"("+customer.phone+")" || customer.email+"("+customer.phone+")",
          value: customer._id,
        });
      }
    }
  }, [isControlled, value, customerRows, selectedValue]);

  // When customerRows change, return them to AsyncSelect via the saved callback
  useEffect(() => {
    if (latestCallback.current && customerRows.length) {
      const options = customerRows.map((cust) => ({
        label: cust.name+"("+cust.phone+")" || cust.email+"("+cust.phone+")",
        value: cust._id,
      }));
      latestCallback.current(options);
      latestCallback.current = null; // reset
    }
  }, [customerRows]);

  // AsyncSelect expects a Promise or a callback
  const loadOptions = (inputValue, callback) => {
    if (!inputValue) return callback([]);

    setInputValue(inputValue);
    latestCallback.current = callback;
  };

  const handleChange = (selectedOption) => {
    // In controlled mode, don't update internal state - let parent control it
    if (!isControlled) {
      setSelectedValue(selectedOption);
    }
    
    // Always call onSelect callback
    if (onSelect) {
      onSelect(selectedOption);
    }
  };

  return (
    <AsyncSelect
    //   cacheOptions
      defaultOptions={false}
      loadOptions={loadOptions}
      onChange={handleChange}
      value={isControlled ? selectedValue : undefined} // Only use value prop in controlled mode
      placeholder={placeholder}
      isClearable
      key={isControlled && value ? `selected-${value}` : isControlled ? 'empty-controlled' : 'uncontrolled'} // Different keys for different modes
      styles={{
        container: (base) => ({
          ...base,
          width: "100%",
          fontSize: "14px",
        }),
      }}
    />
  );
};

export default SearchableCustomerSelect;
